import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import axios from "axios";
import CustomToast from "src/components/CustomToast/CustomToast";
import cmsClient from "src/services/cmsClient";

interface Payload {
	impersonatorUserId: string;
	targetUserId: string;
}

const addTarget = async (payload: Payload) => {
	const response = await cmsClient.post<AdminImpersonationTarget>(
		"/impersonation-assignments/",
		{
			impersonator_user_id: payload.impersonatorUserId,
			target_user_id: payload.targetUserId
		}
	);
	return response.data;
};

const useAddImpersonationTarget = (impersonatorUserId: string) => {
	const queryClient = useQueryClient();

	const onSuccess = () => {
		toast.success(
			<CustomToast type="Success" message="Impersonation target added" />,
			{ hideProgressBar: true }
		);
	};

	const onSettled = () => {
		queryClient.invalidateQueries([
			"impersonation-targets",
			impersonatorUserId
		]);
	};

	const onError = (error: unknown) => {
		let message = "Something went wrong, try again";
		if (axios.isAxiosError(error) && error.response?.data) {
			const data = error.response.data as Record<string, unknown>;
			if (typeof data.detail === "string") {
				message = data.detail;
			} else {
				const flat = Object.values(data).flat().map(String).filter(Boolean);
				if (flat.length > 0) message = flat.join(" ");
			}
		}

		toast.error(<CustomToast type="Error" message={message} />, {
			hideProgressBar: true
		});
	};

	const mutation = useMutation(addTarget, {
		onError,
		onSuccess,
		onSettled
	});

	return mutation;
};

export default useAddImpersonationTarget;
